import { useTranslation } from "react-i18next";
import { FormSection, NumberField } from "@/features/admin/components";
import type { PackagePreviewState } from "../usePackagePreview";

export interface PackageShippingSectionProps {
  state: PackagePreviewState;
}

/**
 * La caja real del paquete: peso y medidas (Anahí, 21-sep-2026).
 *
 * ── Editable, con la SUMA como propuesta ────────────────────────────
 * Un paquete no tiene caja propia en el contrato: sus dimensiones salen
 * de sumar las de sus productos. Eso era el diseño del 27-ago y sigue
 * siendo el valor por omisión — pero un paquete de seis frascos viaja en
 * UNA caja que alguien ya midió, y esa medida real es mejor que
 * cualquier suma. Por eso los cuatro campos se pueden escribir, y por
 * eso la suma va en el `placeholder` de cada uno: lo que se ve en gris
 * es exactamente lo que viajará si se deja vacío.
 *
 * La suma se recalcula con cada producto que se añade o quita, igual que
 * el precio (`usePackagePreview`). Cada campo se resuelve por separado:
 * escribir solo el peso deja las medidas en la suma.
 */
export function PackageShippingSection({ state }: PackageShippingSectionProps) {
  const { t } = useTranslation();
  const sum = state.shippingSum;

  return (
    <FormSection
      title={t("Envío")}
      description={t(
        "La caja real ya empacada. Lo que se deja vacío usa la suma de sus productos, que se ve en gris.",
      )}
      columns={2}
      compact
    >
      <NumberField
        name="shipping.weightGrams"
        label={t("Peso (g)")}
        placeholder={String(sum.weightGrams)}
        hint={t("Gramos enteros.")}
      />
      <NumberField
        name="shipping.lengthCm"
        label={t("Largo (cm)")}
        placeholder={String(sum.lengthCm)}
        inputMode="decimal"
      />
      <NumberField
        name="shipping.widthCm"
        label={t("Ancho (cm)")}
        placeholder={String(sum.widthCm)}
        inputMode="decimal"
      />
      <NumberField
        name="shipping.heightCm"
        label={t("Alto (cm)")}
        placeholder={String(sum.heightCm)}
        inputMode="decimal"
      />
    </FormSection>
  );
}
